import React from "react";
import {
  Box,
  Container,
  Paper,
  Stack,
  Typography,
  Chip,
  Divider,
  Button,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";

import PrivacyTipRoundedIcon from "@mui/icons-material/PrivacyTipRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

const TURQUOISE = "#00B4A6";
const TURQUOISE_DARK = "#00796B";
const GOLD = "#D4AF37";
const BURGUNDY = "#7A1232";
const INK = "#071313";
const WHITE = "#FFFFFF";
const MUTED = "rgba(7, 19, 19, 0.68)";
const LINE = "rgba(7, 19, 19, 0.10)";

const HEADING_FONT = "'Space Grotesk', 'Inter', system-ui, sans-serif";
const BODY_FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";

const privacyPolicyText = `
Privacy Policy

Last updated: 11 June 2026

At Art For My Room, privacy is part of trust. Our website helps people discover art for their rooms and helps artists present original paintings and sculptures. To do this, we may collect and process certain personal information, room images, artwork images and communication details.

This Privacy Policy explains what information we collect, how we use it, who we share it with, and the rights you have.

1. Who we are

Art For My Room is a UK-based website that helps buyers match paintings, prints, sculptures and decorative objects with their room decor. We also provide tools for artists to upload original paintings and sculptures, analyse paintings, and present their work more effectively.

For privacy questions, you can contact us at:

Email: info@artformyroom.com

2. What information we collect

Depending on how you use the website, we may collect the following information.

Information from buyers

When you use the room matching tool, make an offer, buy an artwork or contact us, we may collect:

- your name
- your email address
- your country or general location
- room photos you upload
- artworks you select or place in your room preview
- offer amounts and negotiation messages
- purchase-related information
- messages you send to us

Please avoid uploading room images that show people, private documents, addresses, financial information or anything you do not want processed.

Information from artists

When you upload or submit artwork, we may collect:

- your name or artist name
- your email address
- your country
- artwork images
- artwork title, medium, size, price and description
- sculpture or painting details
- messages and communication history
- information needed to present, list or sell your artwork

Technical information

When you visit the website, we may collect:

- IP address
- browser type and device information
- pages visited
- date and time of visit
- referral source
- cookie and analytics information, where used

3. How we use your information

We use your information to operate and improve Art For My Room.

We may use your information to:

- match uploaded room photos with suitable artworks
- show artwork previews inside a room image
- allow buyers to buy artworks or make offers
- allow artists to upload paintings and sculptures
- create artwork pages and artist pages
- send offer, counter-offer, purchase and confirmation emails
- estimate delivery or postage information
- process payments through our payment provider
- respond to enquiries
- improve website performance and security
- prevent fraud, misuse or spam
- comply with legal, accounting or tax obligations

4. AI and image processing

Art For My Room uses AI-assisted tools to help match rooms with artworks and to support artists with painting analysis, descriptions, presentation notes and SEO tags.

This may involve processing:

- room images uploaded by buyers
- artwork images uploaded by artists
- artwork details such as title, medium, size and price

We use this processing to provide the service requested by the user. We do not sell your room photos. We do not intend to use private room uploads to train public AI models unless this is clearly explained and consented to separately.

5. Lawful basis for processing

Under UK data protection law, we need a lawful basis to process personal information. Depending on the situation, we may rely on:

Contract

We use this when processing is needed to provide a service you requested, such as submitting an offer, buying an artwork, uploading artwork or receiving a room match.

Consent

We use this where you clearly agree to something, such as optional marketing emails or non-essential cookies where applicable.

Legitimate interests

We may use this to operate and improve the website, prevent abuse, respond to enquiries, improve recommendations, protect our business and maintain security.

Legal obligation

We may use this when we need to keep records for tax, accounting, legal or regulatory reasons.

6. Payments

Payments may be processed through a third-party payment provider such as Stripe. When you make a payment, your payment details are handled by the payment provider. We do not aim to store full card numbers on our own servers.

We may receive limited payment information such as payment status, transaction reference, amount, item purchased and contact details needed to complete the order.

7. Who we share information with

We may share limited information with trusted service providers who help us operate the website, including:

- website hosting providers
- image storage providers
- email delivery providers
- payment providers
- analytics providers
- security and error-monitoring tools
- delivery or postage partners where needed

Where an artwork sale or offer involves an artist and a buyer, we may share the information needed to manage that transaction. For example, we may share offer details with the artist and purchase-related details needed to complete the sale.

We do not sell your personal information.

8. International transfers

Some of our service providers may process data outside the United Kingdom. Where this happens, we aim to rely on appropriate safeguards, such as recognised contractual protections or equivalent lawful transfer mechanisms.

9. How long we keep information

We keep personal information only for as long as needed for the purpose it was collected.

As a general guide:

- enquiry messages may be kept while we respond and for a reasonable period afterwards
- offer and purchase records may be kept for legal, accounting and tax purposes
- artist listing information may be kept while the artwork is listed or while the artist relationship continues
- uploaded room images may be kept only as long as needed to provide the matching service, unless you choose to save or continue using them
- technical logs may be kept for security and performance monitoring

We may delete, anonymise or archive information when it is no longer needed.

10. Cookies and similar technologies

Art For My Room may use cookies and similar technologies to make the website work, improve performance, remember preferences, understand website traffic and protect the website.

Some cookies are essential for the website to function. Optional cookies, such as analytics or marketing cookies, should only be used where allowed and, where required, with your consent.

You can control cookies through your browser settings. If a cookie banner or preference tool is available, you can also use it to manage your choices.

11. Your rights

Depending on the situation, you may have rights under UK data protection law, including the right to:

- ask for a copy of your personal information
- ask us to correct inaccurate information
- ask us to delete your information
- ask us to restrict how we use your information
- object to certain uses of your information
- ask for your information to be transferred where applicable
- withdraw consent where we rely on consent

To make a privacy request, contact:

info@artformyroom.com

We may need to verify your identity before responding.

12. Complaints

If you are unhappy with how we handle your personal information, please contact us first so we can try to resolve the issue.

You also have the right to complain to the UK Information Commissioner’s Office, which is the UK regulator for data protection.

13. Security

We take reasonable steps to protect personal information from loss, misuse, unauthorised access, alteration or disclosure.

However, no website or internet transmission is completely secure. Please avoid uploading images or information that contain sensitive private details unless necessary.

14. Children

Art For My Room is not intended for children under 18. If you believe a child has provided personal information to us, please contact us and we will review the matter.

15. Changes to this Privacy Policy

We may update this Privacy Policy from time to time as the website develops, our services change or legal requirements evolve.

The latest version will be published on this page with the updated date.

16. Contact

For privacy questions or requests, contact:

Art For My Room
Email: info@artformyroom.com
`;

export default function PrivacyPolicyPage() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#FBFFFE",
        color: INK,
        fontFamily: BODY_FONT,
        background: `
          radial-gradient(circle at 8% 8%, ${alpha(TURQUOISE, 0.14)}, transparent 30%),
          radial-gradient(circle at 90% 12%, ${alpha(GOLD, 0.18)}, transparent 28%),
          radial-gradient(circle at 86% 82%, ${alpha(BURGUNDY, 0.12)}, transparent 34%),
          linear-gradient(180deg, #ffffff 0%, #f6fffd 50%, #fff8e8 100%)
        `,
      }}
    >
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Button
          component={RouterLink}
          to="/"
          startIcon={<ArrowBackRoundedIcon />}
          sx={{
            mb: 3,
            color: TURQUOISE_DARK,
            fontWeight: 800,
            textTransform: "none",
          }}
        >
          Back home
        </Button>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 8,
            overflow: "hidden",
            border: `1px solid ${LINE}`,
            boxShadow: "0 24px 80px rgba(7, 19, 19, 0.10)",
            bgcolor: WHITE,
          }}
        >
          <Box
            sx={{
              p: { xs: 3, md: 5 },
              color: WHITE,
              background: `linear-gradient(135deg, ${TURQUOISE_DARK}, ${GOLD}, ${BURGUNDY})`,
            }}
          >
            <Stack spacing={2}>
              <Chip
                icon={<PrivacyTipRoundedIcon />}
                label="Commitments"
                sx={{
                  alignSelf: "flex-start",
                  bgcolor: alpha(WHITE, 0.16),
                  color: WHITE,
                  fontWeight: 900,
                  border: `1px solid ${alpha(WHITE, 0.22)}`,
                  "& .MuiChip-icon": { color: WHITE },
                }}
              />

              <Typography
                component="h1"
                sx={{
                  fontFamily: HEADING_FONT,
                  fontSize: { xs: "2.4rem", md: "4rem" },
                  fontWeight: 950,
                  letterSpacing: "-0.07em",
                  lineHeight: 0.95,
                }}
              >
                Privacy Policy
              </Typography>

              <Typography sx={{ color: alpha(WHITE, 0.82), lineHeight: 1.8 }}>
                How Art For My Room handles buyer, artist, artwork and room image information.
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 3, md: 5 } }}>
            <Typography
              component="article"
              sx={{
                whiteSpace: "pre-line",
                color: MUTED,
                lineHeight: 1.85,
                fontSize: "1rem",
                "&::first-line": {
                  color: INK,
                  fontWeight: 950,
                  fontSize: "1.4rem",
                },
              }}
            >
              {privacyPolicyText}
            </Typography>

            <Divider sx={{ my: 4, borderColor: LINE }} />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                component={RouterLink}
                to="/artist-room-matcher"
                variant="contained"
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  bgcolor: TURQUOISE_DARK,
                  "&:hover": { bgcolor: TURQUOISE },
                }}
              >
                Find art for my room
              </Button>

              <Button
                component={RouterLink}
                to="/artist-launch"
                variant="outlined"
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  color: BURGUNDY,
                  borderColor: alpha(BURGUNDY, 0.35),
                }}
              >
                Upload original art
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
